import React, { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { boolean, InferType, object, string } from 'yup';

import { Client, GlobalAccount, GUID, PersonOrgProfile } from '@ndvr/customer-api';
import { Account } from '@ndvr/portfolio-api';

import { FormSheet } from '@/components/FormSheet';
import { getClientTypeAlias, getPersonOrgName } from 'utils';
import { useClientProfilesForHousehold, useCreateClient } from 'views/client/client-queries';
import { useUpdateAccountOwner } from 'views/portfolio/account-queries';
import { FormSelect, requiredStringValidator, Ribbon, Text, Toast } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { useCommandCenterHouseholdProfile } from '../household-queries';

const formSchema = object({
    personOrgGUID: requiredStringValidator,
    needsClient: boolean(),
    startDate: string().when('needsClient', {
        is: true,
        then: (s) => s.required('Start date is required'),
        otherwise: (s) => s.optional(),
    }),
});

type FormType = InferType<typeof formSchema>;

export const ChangeAccountOwnerDrawer = ({
    householdGUID,
    account,
    onClose,
}: {
    householdGUID: GUID<GlobalAccount>;
    account: Account;
    onClose: () => void;
}) => {
    const { data: householdProfile } = useCommandCenterHouseholdProfile(householdGUID);
    const { data: clientProfiles } = useClientProfilesForHousehold(householdGUID);
    const { mutate: updateAccountOwner, isPending } = useUpdateAccountOwner(householdGUID);
    const { mutateAsync: createClientAsync, isPending: isCreatingClient } = useCreateClient(householdGUID);

    const clientsByPersonOrgGUID = useMemo(
        () => new Map<string, Client>(clientProfiles?.map(({ client }) => [client.personOrgGUID, client]) ?? []),
        [clientProfiles],
    );

    const members = useMemo(() => householdProfile?.personOrgFinancialProfiles ?? [], [householdProfile]);

    const hasMembers = members.length > 0;

    const defaultValues = useMemo(
        () => ({ personOrgGUID: account.ownerPersonOrgGUID ?? members[0]?.profile.personOrgGUID }),
        [account.ownerPersonOrgGUID, members],
    );

    const currentOwnerName = useMemo(() => {
        if (account.ownerPersonOrgGUID) {
            const ownerMember = members.find(({ profile }) => profile.personOrgGUID === account.ownerPersonOrgGUID);
            if (ownerMember) return getPersonOrgName(ownerMember.profile.personOrg);
            return 'Unlisted account owner';
        }
        return 'Unassigned';
    }, [account.ownerPersonOrgGUID, members]);

    const onSubmit = async (formValues: FormType, close: () => void) => {
        let clientGUID = clientsByPersonOrgGUID.get(formValues.personOrgGUID)?.clientGUID;

        if (!clientGUID) {
            const client = await createClientAsync({
                personOrgGUID: formValues.personOrgGUID,
                startDate: formValues.startDate!,
            });
            clientGUID = client?.clientGUID;
        }

        if (!clientGUID) {
            Toast.error('Failed to resolve account owner. Please contact support.', 'error');
            return;
        }

        updateAccountOwner(
            { accountGroupGUID: account.accountGroupGUID, accountGUID: account.accountGUID, clientGUID },
            { onSuccess: close },
        );
    };

    return (
        <FormSheet
            title={`Change account owner for ${account.accountName.plainText}`}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={formSchema}
            defaultValues={defaultValues}
            pending={isPending || isCreatingClient}
        >
            <Text helper $themeColor="subtle">
                Current owner: {currentOwnerName}
            </Text>
            <OwnerSelectFields members={members} clientsByPersonOrgGUID={clientsByPersonOrgGUID} />
            {!hasMembers ? (
                <Ribbon intent="warning">
                    <Text>
                        This household has no members yet. Add a member to the household before changing the account
                        owner.
                    </Text>
                </Ribbon>
            ) : (
                <Ribbon intent="info">
                    <Text>
                        Account owners must belong to the same household. Changing the owner updates reporting
                        relationships and permissions for this account.
                    </Text>
                </Ribbon>
            )}
        </FormSheet>
    );
};

const OwnerSelectFields = ({
    members,
    clientsByPersonOrgGUID,
}: {
    members: { profile: PersonOrgProfile }[];
    clientsByPersonOrgGUID: Map<string, Client>;
}) => {
    const { setValue } = useFormContext();
    const [personOrgGUID] = useWatch({ name: ['personOrgGUID'] });

    const needsClient = personOrgGUID ? !clientsByPersonOrgGUID.has(personOrgGUID) : false;

    useEffect(() => {
        setValue('needsClient', needsClient);
    }, [needsClient, setValue]);

    return (
        <>
            <FormSelect name="personOrgGUID" label="New Account Owner" disabled={!members.length}>
                <option value="" disabled>
                    Select account owner
                </option>
                {members.map(({ profile }) => (
                    <option key={profile.personOrgGUID} value={profile.personOrgGUID}>
                        {getPersonOrgName(profile.personOrg)} ({getClientTypeAlias(profile.personOrg.personOrgType)})
                    </option>
                ))}
            </FormSelect>
            {needsClient && <FormDateInput name="startDate" label="Client agreement start date" />}
        </>
    );
};
